import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { CandidateStatus } from "@/types/candidates"

/**
 * OKS-17 / OKS-18 — Shared status primitive for candidate pipeline.
 *
 * Distinct from `components/status-badge.tsx` (which is the
 * invitation-lifecycle primitive: `pending|expired|accepted|revoked|active`).
 * This one covers the 9-value candidate pipeline:
 * `new|reviewed|pending_brand_validation|approved|contacted|invited|joined|
 * rejected|blocklisted`.
 *
 * **Single source of truth across both pages.** OKS-17 list rows + status
 * tabs render this component; OKS-18 detail header + status timeline
 * inherit the same color tokens (UX Spec §Screen Specifications status
 * badge color mapping table — locked).
 *
 * **Color is never the sole indicator** — every variant always renders the
 * text label. Screen reader announces the localized status word via
 * `aria-label`. `data-state` exposes the prop value verbatim so Vitest
 * assertions key off the prop, not derived state.
 *
 * **Accessibility:** WCAG-AA contrast on every variant (verified against
 * shadcn defaults + amber/emerald/violet/red/zinc tokens at the lightness
 * tier the UX Spec specifies — light bg + dark text ≥ 4.5:1 on every
 * status).
 */

interface StatusVisual {
  /** Tailwind class string — uses standard color tokens, never raw hex. */
  className: string
}

const STATUS_VISUAL: Record<CandidateStatus, StatusVisual> = {
  new: {
    className:
      "border-blue-200 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-700/60",
  },
  reviewed: {
    className:
      "border-amber-200 bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700/60",
  },
  // OKS-948 — orange, deliberately adjacent to Reviewed's amber: the two are
  // the pipeline's "waiting" pair. Colour is never the sole indicator (the
  // label always renders + a localized aria-label), so the adjacency is a
  // legibility question, not an a11y one. UX Spec OQ8 delegates the call: if
  // orange and amber read as one colour at badge size in the light theme,
  // swap this row to cyan — no other file moves.
  pending_brand_validation: {
    className:
      "border-orange-200 bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-700/60",
  },
  approved: {
    className:
      "border-emerald-200 bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-700/60",
  },
  // OKS-948 — teal. Distinct from `joined`/`approved` emerald (the "done"
  // family) because Contacted is a waiting state, not a success one.
  contacted: {
    className:
      "border-teal-200 bg-teal-100 text-teal-900 dark:bg-teal-900/30 dark:text-teal-200 dark:border-teal-700/60",
  },
  invited: {
    className:
      "border-violet-200 bg-violet-100 text-violet-900 dark:bg-violet-900/30 dark:text-violet-200 dark:border-violet-700/60",
  },
  joined: {
    className:
      "border-emerald-300 bg-emerald-200 text-emerald-950 dark:bg-emerald-800/50 dark:text-emerald-100 dark:border-emerald-600/60",
  },
  rejected: {
    className:
      "border-red-200 bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700/60",
  },
  blocklisted: {
    className:
      "border-zinc-300 bg-zinc-200 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 dark:border-zinc-600/60",
  },
}

/**
 * Resolve the `candidates.status.*` i18n label via literal `t()` arguments
 * so the inertia_i18n health scanner can statically prove every key is
 * reachable. Dynamic template strings (`t(\`candidate_status.${status}\`)`)
 * silently bypass the scanner. Same pattern as `roleLabel` /
 * `statusLabel` / `userTierLabel`.
 *
 * OKS-948 — EXPORTED so the revert confirmation can name its destination
 * through this same switch. That keeps the `approved` → "To contact" relabel
 * in one place: a separate lookup in the dialog would silently drift the day
 * the label changes again.
 */
export function candidateStatusLabel(
  t: ReturnType<typeof useTranslation>["t"],
  status: CandidateStatus,
): string {
  switch (status) {
    case "new":
      return t("candidates.status.new")
    case "reviewed":
      return t("candidates.status.reviewed")
    case "pending_brand_validation":
      return t("candidates.status.pending_brand_validation")
    case "approved":
      return t("candidates.status.approved")
    case "contacted":
      return t("candidates.status.contacted")
    case "invited":
      return t("candidates.status.invited")
    case "joined":
      return t("candidates.status.joined")
    case "rejected":
      return t("candidates.status.rejected")
    case "blocklisted":
      return t("candidates.status.blocklisted")
  }
}

interface CandidateStatusBadgeProps {
  status: CandidateStatus
  className?: string
  /**
   * Override the default `data-testid` for surfaces that scope the badge
   * to a parent (e.g. `candidate-list-row-status-{id}`,
   * `candidate-detail-status-badge`). Defaults to
   * `candidate-status-badge` so generic uses still expose a hook.
   */
  testId?: string
}

export function CandidateStatusBadge({
  status,
  className,
  testId = "candidate-status-badge",
}: CandidateStatusBadgeProps) {
  const { t } = useTranslation()
  const visual = STATUS_VISUAL[status]
  const label = candidateStatusLabel(t, status)

  return (
    <Badge
      variant="outline"
      role="status"
      aria-label={label}
      data-testid={testId}
      data-state={status}
      className={cn(visual.className, className)}
    >
      {label}
    </Badge>
  )
}
